import { createReducer, on } from '@ngrx/store'
import { WritableDraft, produce } from 'immer'
import { random as _random } from 'lodash/fp'
import { v4 as uuidV4 } from 'uuid'

import { TemplateConfigInterface } from '@flowx/core-sdk'
import { ScriptingLanguage } from '@flowx/designer/modules/core/dictionary/common.types'
import {
  ActionDefinition,
  NodeDefinition,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { CodeEditorSelection } from '@flowx/designer/modules/monaco-code-editor/types'
import {
  UiDesignerComputedModalActions,
  UiDesignerConfigPanelActions,
  UiDesignerExpressionModalActions,
} from '@flowx/designer/modules/ui-designer/ui-designer.actions'
import { TemplateConfigActions } from '@flowx/designer/shared/store/actions/template-config.actions'

import {
  AiActions,
  AiConversationServiceActions,
  AiPermissionActions,
  BreadcrumbActions,
  CommandPaletteActions,
  CtaActions,
  FloatingWindowActions,
  KeyboardActions,
  NodeMenuActions,
  SequenceMenuActions,
} from './ai.actions'

import { AiAnalystUtils } from '../agents/analyst/services/analyst.service'
import { AiAnalystActions } from '../agents/analyst/store/analyst.actions'
import { AiAssistantActions } from '../agents/assistant/store/assistant.actions'
import { AiTemplateConfig, DesignerService } from '../agents/designer/services/designer.service'
import { AiDesignerActions } from '../agents/designer/store/designer.actions'
import {
  FixWarningsBusinessRuleResponse,
  GenerateBusinessRuleResponse,
  GenerateExpressionResponse,
} from '../agents/developer/api/developer.api'
import { AiDocumentsActions } from '../agents/documents/store/documents.actions'
import { AiChatBubbleActions, TypeAreaActions } from '../conversation/conversation.actions'
import {
  AiAgent,
  AiDesignerContext,
  AiPermissions,
  AiProcess,
  AiProcessEntities,
  BusinessRuleContext,
  ContextDocument,
  DataModelDraft,
  DocumentContextReference,
  ChatMessage,
  MessageType,
  Position,
  UploadContextFile,
  scriptingLanguageWithGeneration,
} from '../types/ai.types'

export type AiConversationMetadata = {
  id: string
  threadId: string
  lastMessageId: string
  status: 'idle' | 'streaming'
  target: {
    nodeId: NodeDefinition['id']
    actionId: ActionDefinition['id']
    businessRuleIndex: number
  }
}

export type AiState = {
  permissions: AiPermissions
  fabPosition: Position
  businessRuleContext: BusinessRuleContext
  codeSelectionContext: CodeEditorSelection

  minimized: boolean

  conversation: AiConversationMetadata
  selectedAgent: AiAgent | null
  selectedDocument: ContextDocument | null
  selectedDocumentContext: DocumentContextReference | null
  selectedDesignerUserTaskAreaId: number | null
  selectedDesignerTemplateConfigId: number | null
  sessionId: string | null
  isAiInteractionAllowed: boolean
  isFileUploadAllowed: boolean
  isChatConversationAllowed: boolean
  messages: ChatMessage[]
  uploadingDocuments: UploadContextFile[]

  pendingDocumentsLoading: boolean
  pendingAiResponse: boolean
  pendingFixWarningsResponse: boolean
  pendingFixErrorResponse: boolean
  pendingKeepDraftSave: boolean
  streamingAiResponse: boolean

  startedFromSequence: number | null
  generatedSequenceProcess: AiProcess | null
  generatedProcessEntities: AiProcessEntities | null
  generatedProcessPreviewHeader: {
    containerTransform?: string
    contentTransform?: string
  } | null
  generatedUI: AiTemplateConfig | null
  generatedUIDataModelChanges: DataModelDraft | null
  generatedUIActionMappings: AiDesignerContext['actionMappings'] | null
  generatedBusinessRule: GenerateBusinessRuleResponse | null
  generatedBusinessRuleCode: string | null
  generatedFixWarningsBusinessRule: FixWarningsBusinessRuleResponse | null
  generatedFixErrorBusinessRule: FixWarningsBusinessRuleResponse | null
  generatedExpression: GenerateExpressionResponse | null
  generatedImprovedCode: string | null
  generatedAddComment: string | null

  buttonTemplateIdMappings: AiDesignerContext['actionMappings'] | null
  buttonFormMappings: Record<
    number,
    { buttonFlowxUuid: string; forms: { id: number; flowxUuid: string }[] }
  > | null
}

export const initialState: AiState = {
  permissions: {
    showAssistantAgent: false,
    showAnalystAgent: false,
    showDesignerAgent: false,
    showDeveloperAgent: false,
    canViewDocuments: false,
    canUploadDocuments: false,
  },
  fabPosition: { right: 24, bottom: 80 },
  businessRuleContext: null,
  codeSelectionContext: null,

  minimized: true,

  conversation: {
    id: null,
    threadId: null,
    lastMessageId: null,
    status: 'idle',
    target: {
      nodeId: null,
      actionId: null,
      businessRuleIndex: null,
    },
  },

  selectedAgent: null,
  selectedDocument: null,
  selectedDocumentContext: null,
  selectedDesignerUserTaskAreaId: null,
  selectedDesignerTemplateConfigId: null,
  sessionId: null,
  isAiInteractionAllowed: true,
  isFileUploadAllowed: true,
  isChatConversationAllowed: true,
  messages: [],
  uploadingDocuments: [],

  pendingDocumentsLoading: false,
  pendingAiResponse: false,
  pendingFixWarningsResponse: false,
  pendingFixErrorResponse: false,
  pendingKeepDraftSave: false,
  streamingAiResponse: false,

  startedFromSequence: null,
  generatedSequenceProcess: null,
  generatedProcessEntities: null,
  generatedProcessPreviewHeader: null,
  generatedUI: null,
  generatedUIDataModelChanges: null,
  generatedUIActionMappings: null,
  generatedBusinessRule: null,
  generatedBusinessRuleCode: null,
  generatedFixWarningsBusinessRule: null,
  generatedFixErrorBusinessRule: null,
  generatedExpression: null,
  generatedImprovedCode: null,
  generatedAddComment: null,

  buttonTemplateIdMappings: null,
  buttonFormMappings: null,
}

export const aiReducer = createReducer(
  initialState,

  // Configs
  on(AiPermissionActions.loadAiPermissionsSuccess, (state, { permissions }) =>
    produce(state, (draft) => {
      draft.permissions = permissions
    })
  ),
  on(AiActions.fabMoved, (state, { position }) =>
    produce(state, (draft) => {
      draft.fabPosition = position
    })
  ),
  on(AiActions.businessRuleContextChanged, (state, { businessRuleContext }) =>
    produce(state, (draft) => {
      draft.businessRuleContext = businessRuleContext
      if (
        state.selectedAgent &&
        !scriptingLanguageWithGeneration.includes(
          businessRuleContext?.language as ScriptingLanguage
        )
      ) {
        /* TODO Command Center - see if this need to exit command center mode*/
        // draft.minimized = true
        closeChat(draft)
      }
    })
  ),
  on(AiActions.actionEditDestroyed, (state) =>
    produce(state, (draft) => {
      draft.businessRuleContext = null

      if (state.selectedAgent === AiAgent.DEVELOPER) {
        /* TODO Command Center - see if this need to exit command center mode*/
        // draft.minimized = true
        closeChat(draft)
      }
    })
  ),
  on(
    UiDesignerExpressionModalActions.componentDestroy,
    UiDesignerComputedModalActions.componentDestroy,
    (state) =>
      produce(state, (draft) => {
        draft.businessRuleContext = null
      })
  ),

  // Navigation
  on(AiActions.pageAgentChanged, (state) =>
    produce(state, (draft) => {
      /* TODO Command Center - see if this need to exit command center mode*/
      // draft.commandCenterActivated = false
      // draft.minimized = true
      closeChat(draft)
    })
  ),

  // on(AiActions.fabClick, (state) =>
  //   produce(state, (draft) => {
  //     draft.minimized = false
  //   })
  // ),
  on(
    TemplateConfigActions.singleSelectTemplateConfig,
    FloatingWindowActions.minimizeClick,
    AiActions.outsideOfAiChatClick,
    (state) =>
      produce(state, (draft) => {
        /* TODO Command Center - see if this need to exit command center mode*/
        // draft.minimized = true

        const isLoading = state.pendingAiResponse || state.pendingKeepDraftSave

        if (!isLoading && hasOnlySystemMsg(state.messages)) {
          closeChat(draft)
        }
      })
  ),

  // Documents
  on(AiDocumentsActions.loadDocuments, (state) =>
    produce(state, (draft) => {
      draft.pendingDocumentsLoading = true
    })
  ),
  on(AiDocumentsActions.loadDocumentsSuccess, (state, { documents }) =>
    produce(state, (draft) => {
      draft.pendingDocumentsLoading = false
      if (documents.length) {
        draft.messages.push({
          message: state.permissions.canUploadDocuments ? `Or you can upload a new document:` : '',
          documents: {
            message: 'Here are the existing documents on this application:',
            files: documents,
          },
          type: MessageType.SystemMsg,
          actions: {
            documentUpload: state.permissions.canUploadDocuments,
          },
        })
      } else {
        draft.messages.push({
          message: `You don't have any documents uploaded yet. ${state.permissions.canUploadDocuments ? 'Upload a document to get started.' : 'Try generating from an prompt'}`,
          type: MessageType.SystemMsg,
          actions: {
            documentUpload: state.permissions.canUploadDocuments,
          },
        })
      }
    })
  ),
  on(AiChatBubbleActions.documentClick, (state, { document }) =>
    produce(state, (draft) => {
      draft.selectedDocument = document
      draft.messages.push({
        message: `Here is a summary of your selected document:`,
        generators: {
          summary: document.summary,
          documentId: document.documentId,
          items: document[state.selectedAgent] ?? [],
          agent: state.selectedAgent,
        },
        type: MessageType.SystemMsg,
      })
    })
  ),

  on(AiActions.contextDocumentCloseClick, AiActions.closeContextDocumentConfirmed, (state) =>
    produce(state, (draft) => {
      draft.selectedDocument = null
      draft.selectedDocumentContext = null
    })
  ),

  // Commands & CTA
  on(CommandPaletteActions.askAssistantCommand, (state, { question }) =>
    produce(state, (draft) => {
      openChat(draft, {
        selectedAgent: AiAgent.ASSISTANT,
        initialMessage: {
          message: question,
          type: MessageType.UserMsg,
        },
      })
    })
  ),
  on(CommandPaletteActions.generateNewProcessCommand, CtaActions.processDesignerCtaClick, (state) =>
    produce(state, (draft) => {
      if (canSelectAgent(state)) {
        openChatWithDocumentGenerationMsg(draft, {
          selectedAgent: AiAgent.ANALYST,
        })
      }
    })
  ),
  on(
    CommandPaletteActions.generateExistingProcessCommand,
    NodeMenuActions.generateProcessNodeMenuAction,
    (state) =>
      produce(state, (draft) => {
        if (canSelectAgent(state)) {
          openChat(draft, {
            selectedAgent: AiAgent.ANALYST,
            initialMessage: {
              message:
                'Describe the changes you want as detailed as possible including nodes and swimlane names.',
              type: MessageType.SystemMsg,
            },
            isFileUploadAllowed: false,
          })
        }
      })
  ),
  on(
    CommandPaletteActions.generateNewUICommand,
    CtaActions.uiDesignerCtaClick,
    (state, { userTaskNavigationAreaId }) =>
      produce(state, (draft) => {
        if (canSelectAgent(state)) {
          draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
          openChatWithDocumentGenerationMsg(draft, {
            selectedAgent: AiAgent.DESIGNER,
          })
        }
      })
  ),
  on(
    AiDesignerActions.uiDesignerNodeAiClick,
    (state, { userTaskNavigationAreaId, selectedTemplateConfigId }) =>
      produce(state, (draft) => {
        closeChat(draft)
        draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
        draft.selectedDesignerTemplateConfigId = selectedTemplateConfigId
        openChat(draft, {
          selectedAgent: AiAgent.DESIGNER,
          initialMessage: {
            message: `I am here to support you in generating UI in this area.`,
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: state.permissions.canUploadDocuments,
            },
            type: MessageType.SystemMsg,
          },
        })
      })
  ),
  on(
    // TODO: Check if we need to re-implement for this action also, or if we need to remove it
    // CommandPaletteActions.generateNewBusinessRuleCommand,
    CtaActions.generateBusinessRuleCtaClick,
    (state, props) =>
      produce(state, (draft) => {
        if (canSelectAgent(state)) {
          openChatWithDocumentGenerationMsg(draft, {
            selectedAgent: AiAgent.DEVELOPER,
          })
        }
        draft.conversation.target.nodeId = props.nodeId
        draft.conversation.target.actionId = props.actionId
        draft.conversation.target.businessRuleIndex = props.businessRuleIndex
      })
  ),
  on(NodeMenuActions.generateUiNodeMenuActionTransitionEnd, (state, { userTaskNavigationAreaId }) =>
    produce(state, (draft) => {
      if (canSelectAgent(state)) {
        // draft.minimized = false
        draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
        openChatWithDocumentGenerationMsg(draft, {
          selectedAgent: AiAgent.DESIGNER,
        })
      }
    })
  ),
  on(NodeMenuActions.generateBusinessRuleNodeMenuActionTransitionEnd, (state) =>
    produce(state, (draft) => {
      if (canSelectAgent(state)) {
        // draft.minimized = false
        openChatWithDocumentGenerationMsg(draft, {
          selectedAgent: AiAgent.DEVELOPER,
        })
      }
    })
  ),
  on(CtaActions.explainBusinessRuleCtaClick, (state) =>
    produce(state, (draft) => {
      if (canSelectAgent(state)) {
        openChat(draft, {
          selectedAgent: AiAgent.DEVELOPER,
          initialMessage: {
            message: 'Explain business rule',
            type: MessageType.UserMsg,
          },
          isChatConversationAllowed: false,
        })
      }
    })
  ),
  on(
    UiDesignerExpressionModalActions.generateClick,
    UiDesignerConfigPanelActions.expressionGenerateClick,
    (state) =>
      produce(state, (draft) => {
        if (canSelectAgent(state)) {
          if (state.businessRuleContext) {
            openChatWithDocumentGenerationMsg(draft, {
              selectedAgent: AiAgent.DEVELOPER,
            })
          } else {
            draft.selectedDocument = null
            draft.selectedDocumentContext = null

            openChat(draft, {
              selectedAgent: AiAgent.DEVELOPER,
              initialMessage: {
                message: 'Specify what expression you want to generate.',
                type: MessageType.SystemMsg,
              },
              isFileUploadAllowed: false,
            })
          }
        }
      })
  ),
  on(
    CtaActions.processDesignerCtaClick,
    CtaActions.uiDesignerCtaClick,
    CtaActions.generateBusinessRuleCtaClick,
    CtaActions.explainBusinessRuleCtaClick,
    SequenceMenuActions.generateProcess,
    NodeMenuActions.generateProcessNodeMenuAction,
    AiDesignerActions.uiDesignerNodeAiClick,
    UiDesignerExpressionModalActions.generateClick,
    UiDesignerConfigPanelActions.expressionGenerateClick,
    (state) => state
  ),
  // on(CtaActions.fixBusinessRuleWarningsCtaClick, (state) =>
  //   produce(state, (draft) => {
  //     if (canSelectAgent(state)) {
  //       draft.isAiInteractionAllowed = false
  //       openChat(draft, { selectedAgent: AiAgent.DEVELOPER })
  //     }
  //   })
  // ),
  on(CtaActions.fixBusinessRuleErrorCtaClick, (state) =>
    produce(state, (draft) => {
      if (canSelectAgent(state)) {
        draft.isAiInteractionAllowed = false
        openChat(draft, { selectedAgent: AiAgent.DEVELOPER })
      }
    })
  ),

  // Breadcrumbs
  on(BreadcrumbActions.backClick, (state) =>
    produce(state, (draft) => {
      closeChat(draft)
    })
  ),

  // TypeArea
  on(TypeAreaActions.promptSubmit, (state, { text, files }) =>
    produce(state, (draft) => {
      const message = {
        message: text,
        ...(files?.length ? { files } : {}),
        type: MessageType.UserMsg,
      }

      if (!state.selectedAgent) {
        openChat(draft, {
          selectedAgent: AiAgent.ASSISTANT,
          initialMessage: message,
        })
      } else {
        draft.messages.push(message)
      }
    })
  ),
  // on(AiConversationServiceActions.businessRuleStreamUpdate, (state, { update }) =>
  //   produce(state, (draft) => {
  //     if (draft.generatedBusinessRuleCode !== null) {
  //       draft.generatedBusinessRuleCode += update
  //     } else {
  //       draft.generatedBusinessRuleCode = update
  //     }
  //     draft.conversation.status = 'streaming'
  //   })
  // ),
  on(
    AiConversationServiceActions.conversationMessageStreamError,
    AiConversationServiceActions.conversationMessageStreamEnded,
    (state) =>
      produce(state, (draft) => {
        draft.pendingAiResponse = false
      })
  ),

  // Designer
  on(AiDesignerActions.mapTemplateConfigComponentsToDataModel, (state, { templateConfig }) =>
    produce(state, (draft) => {
      if (draft.generatedUIDataModelChanges) {
        draft.generatedUIDataModelChanges =
          DesignerService.updateDataModelWithTemplateConfigIdentifiers(
            draft.generatedUIDataModelChanges,
            templateConfig
          )
      }
    })
  ),
  // TODO: To remove on UI action mappings refactoring w/ BE endpoint
  on(AiDesignerActions.mapAiLinkingsToTemplateConfig, (state, { templateConfig }) =>
    produce(state, (draft) => {
      if (!draft.generatedUI || !draft.generatedUIActionMappings) {
        return
      }

      // Recreate the draft template config structure from the last generated UI
      const draftTemplateConfig = DesignerService.mapAiUiToTemplateConfig(
        () => _random(Date.now(), Number.MAX_SAFE_INTEGER),
        uuidV4,
        draft.generatedUI
      ) as TemplateConfigInterface

      const persistedRoot = Array.isArray(templateConfig) ? templateConfig[0] : templateConfig

      if (!persistedRoot) {
        return
      }

      const keyToId = DesignerService.buildKeyToIdMap(draftTemplateConfig, persistedRoot)

      draft.buttonTemplateIdMappings = DesignerService.linkUiActionMappings(
        draft.generatedUIActionMappings,
        keyToId
      )
    })
  ),
  on(AiDesignerActions.mapTemplateConfigComponentsToAiLinkings, (state, { templateConfig }) =>
    produce(state, (draft) => {
      if (!draft.buttonTemplateIdMappings) {
        return
      }

      draft.buttonFormMappings = DesignerService.mapTemplateConfigComponentsToAiLinkingElements(
        templateConfig,
        draft.buttonTemplateIdMappings
      )
    })
  ),
  on(
    AiDesignerActions.discardTemplateConfigDraft,
    AiDesignerActions.discardDataModelDraftSuccess,
    AiDesignerActions.keepDataModelDraftSuccess,
    (state) =>
      produce(state, (draft) => {
        /* TODO Command Center - see if this need to exit command center mode*/
        // draft.minimized = true
        closeChat(draft)
      })
  ),

  // Analyst
  on(AiAnalystActions.generateProcessSuccess, (state, { response, aiEntities }) =>
    produce(state, (draft) => {
      if (response.answer) {
        const processStateSnapshot = AiAnalystUtils.getProcessStateSnapshot()
        const changes = AiAnalystUtils.getProcessChanges(
          aiEntities,
          processStateSnapshot.nodes,
          processStateSnapshot.boundaryEvents
        )

        draft.generatedProcessEntities = aiEntities
        draft.messages.push({
          message: response.answer.md,
          context: {
            ...response,
            changes,
          },
          type: MessageType.AiMsg,
        })
      } else if (response.chat) {
        draft.messages.push({
          message: response.chat,
          type: MessageType.SystemMsg,
        })
      } else {
        draft.messages.push(generateChatErrorMessage(response.error_message))
      }
    })
  ),
  // on(AiAnalystActions.insertInProcessSuccess, (state, { response, aiEntities }) =>
  //   produce(state, (draft) => {
  //     draft.generatedSequenceProcess = response.answer
  //   })
  // ),
  // TODO: GENERATE_SEQUENCE analystGenerateFromSequenceSuccess
  on(AiAnalystActions.discardProcessDraft, AiAnalystActions.keepProcessDraftSuccess, (state) =>
    produce(state, (draft) => {
      /* TODO Command Center - see if this need to exit command center mode*/
      // draft.minimized = true
      closeChat(draft)
    })
  ),

  // Assistant
  on(AiAssistantActions.getAnswerEventStream, (state, { response }) =>
    produce(state, (draft) => {
      draft.streamingAiResponse = true
      const { message, context, sources } = response
      const lastMessageDraft = draft.messages[draft.messages.length - 1]
      const isLastMessageOfTypeAi = lastMessageDraft.type === MessageType.AiMsg
      if (isLastMessageOfTypeAi) {
        Object.assign(lastMessageDraft, response)
        lastMessageDraft.message = message
        lastMessageDraft.context = lastMessageDraft.context || context
        lastMessageDraft.sources = sources
        lastMessageDraft.type = MessageType.AiMsg
      } else {
        draft.messages.push({
          ...response,
          message,
          context,
          sources,
          type: MessageType.AiMsg,
        })
      }
    })
  ),
  on(
    AiAssistantActions.getAnswerStreamCompleted,
    AiAssistantActions.getAnswerStreamFailure,
    AiActions.cancelPendingRequest,
    (state) =>
      produce(state, (draft) => {
        draft.streamingAiResponse = false
        draft.isAiInteractionAllowed = true
      })
  ),

  on(
    AiAssistantActions.getAnswerStreamCompleted,
    AiAssistantActions.getAnswerStreamFailure,
    AiAnalystActions.generateProcessSuccess,
    AiAnalystActions.generateProcessFailure,
    AiActions.cancelPendingRequest,
    (state) =>
      produce(state, (draft) => {
        draft.pendingAiResponse = false
        draft.pendingDocumentsLoading = false
      })
  ),
  on(AiActions.cancelPendingRequest, (state) =>
    produce(state, (draft) => {
      draft.pendingFixWarningsResponse = false
    })
  ),
  on(AiActions.cancelPendingRequest, (state) =>
    produce(state, (draft) => {
      draft.pendingFixErrorResponse = false
    })
  ),
  on(AiActions.cancelPendingRequest, (state) =>
    produce(state, (draft) => {
      draft.uploadingDocuments = []
    })
  ),

  on(AiActions.businessRuleContextReset, (state, { businessRuleContext }) =>
    produce(state, (draft) => {
      draft.businessRuleContext = businessRuleContext
    })
  ),

  // Keyboard
  // on(KeyboardActions.escape, (state, { isBreadcrumbsBackDisabled }) =>
  //   produce(state, (draft) => {
  //     const canCloseChat = !state.pendingAiResponse && !state.generatedProcess

  //     if (!isBreadcrumbsBackDisabled) {
  //       if (state.selectedAgent && canCloseChat) {
  //         closeChat(draft)
  //       } else {
  //         // draft.minimized = true
  //       }
  //     }
  //   })
  // ),
  on(KeyboardActions.searchShortcut, (state) =>
    produce(state, (draft) => {
      draft.minimized = false
    })
  ),

  on(AiActions.canvasTransform, (state, { generatedProcessPreviewHeader }) =>
    produce(state, (draft) => {
      draft.generatedProcessPreviewHeader = generatedProcessPreviewHeader
    })
  ),

  on(AiActions.sendFeedback, (state, { feedback }) =>
    produce(state, (draft) => {
      const messageDraft = draft.messages.find(
        (message) => message.context?.request_id === feedback.requestId
      )
      messageDraft.feedback = feedback.feedbackType
    })
  ),

  on(AiActions.closeChatConfirmed, (state) =>
    produce(state, (draft) => {
      closeChat(draft)
    })
  ),

  on(AiActions.cancelPendingRequestClick, (state) =>
    produce(state, (draft) => {
      draft.messages.push({
        message: `You've canceled your request`,
        type: MessageType.SystemMsg,
      })
    })
  ),

  // Errors
  on(
    AiAssistantActions.getAnswerStreamFailure,
    AiAnalystActions.generateProcessFailure,
    AiAnalystActions.keepProcessDraftFailure,
    AiDesignerActions.saveDataModelDraftFailure,
    AiDesignerActions.linkTemplateConfigKeysFailure,
    (state, { statusCode, errorMsg }) =>
      produce(state, (draft) => {
        draft.messages.push(generateChatErrorMessage(errorMsg, statusCode))
      })
  )
)

function canSelectAgent(state: AiState): boolean {
  const { selectedAgent, pendingAiResponse, pendingKeepDraftSave, messages } = state

  const isLoading = pendingAiResponse || pendingKeepDraftSave
  const isChangeableAgent =
    !selectedAgent || selectedAgent === AiAgent.ASSISTANT || hasOnlySystemMsg(messages)

  return !isLoading && isChangeableAgent
}

function hasOnlySystemMsg(messages: ChatMessage[]): boolean {
  return !messages.find(({ type }) => type !== MessageType.SystemMsg)
}

function openChat(
  draft: WritableDraft<AiState>,
  state: {
    selectedAgent: AiAgent
    initialMessage?: ChatMessage
    isChatConversationAllowed?: boolean
    isFileUploadAllowed?: boolean
  }
): void {
  const { selectedAgent, initialMessage, isChatConversationAllowed, isFileUploadAllowed } = state
  draft.selectedAgent = selectedAgent
  draft.sessionId = uuidV4()
  draft.messages = initialMessage ? [initialMessage] : []
  if (typeof isChatConversationAllowed !== 'undefined') {
    draft.isChatConversationAllowed = isChatConversationAllowed
  }
  if (typeof isFileUploadAllowed !== 'undefined') {
    draft.isFileUploadAllowed = isFileUploadAllowed
  }
}

function openChatWithDocumentGenerationMsg(
  draft: WritableDraft<AiState>,
  state: {
    selectedAgent: AiAgent
  }
): void {
  const initialMessage = {
    message: `Specify what you want to generate. ${draft.permissions.canViewDocuments ? 'You can also start from context documents.' : ''}`,
    actions: getActionsPermissions({ ...draft, ...state }),
    type: MessageType.SystemMsg,
  }

  openChat(draft, { ...state, initialMessage })
}

function getActionsPermissions(state: AiState): ChatMessage['actions'] {
  return {
    checkDocumentContext:
      state.permissions.canViewDocuments && state.selectedAgent !== AiAgent.ASSISTANT,
    imageUpload: state.selectedAgent !== AiAgent.DEVELOPER && state.permissions.canUploadDocuments,
    bpmnUpload: state.selectedAgent === AiAgent.ANALYST && state.permissions.canUploadDocuments,
  }
}

function closeChat(draft: WritableDraft<AiState>): void {
  newChatSession(draft)

  draft.selectedAgent = null
  draft.sessionId = null
  draft.messages = []
}

function newChatSession(draft: WritableDraft<AiState>): void {
  draft.selectedDocument = null
  draft.selectedDocumentContext = null
  draft.selectedDesignerUserTaskAreaId = null
  draft.selectedDesignerTemplateConfigId = null
  draft.sessionId = uuidV4()
  draft.isAiInteractionAllowed = true
  draft.isFileUploadAllowed = true
  draft.isChatConversationAllowed = true
  draft.startedFromSequence = null
  draft.generatedSequenceProcess = null
  draft.generatedProcessEntities = null
  draft.generatedProcessPreviewHeader = null
  draft.generatedUI = null
  draft.generatedUIDataModelChanges = null
  draft.generatedBusinessRule = null
  draft.generatedFixWarningsBusinessRule = null
  draft.generatedFixErrorBusinessRule = null
  draft.generatedExpression = null
  draft.generatedImprovedCode = null
  draft.generatedAddComment = null
}

function generateChatErrorMessage(errorMsg, statusCode: number = 400): ChatMessage {
  const message = extractMessageFromError(errorMsg, statusCode)

  return {
    message,
    type: MessageType.ErrorMsg,
  }
}

function extractMessageFromError(
  error:
    | string
    | { title: string; defaultMessage: string; error_message: { client_message: string } },
  statusCode: number = 400
): string {
  const CONTINUE_MESSAGE = `You can continue the conversation or start a new chat.`

  if (statusCode >= 500 || statusCode === 0) {
    return `Something went wrong on our end. ${CONTINUE_MESSAGE}`
  } else {
    const DEFAULT_MESSAGE = `Something went wrong with your request. Please check the information you entered and try again.`

    if (typeof error === 'string') {
      return `${error} ${CONTINUE_MESSAGE}`
    }

    const serverMessage =
      error?.title ?? error?.defaultMessage ?? error?.error_message?.client_message

    return `${serverMessage ?? DEFAULT_MESSAGE} ${CONTINUE_MESSAGE}`
  }
}
